<template>
	<ext-dialog
		v-model="visible"
		:title="dialogTitle"
		:primaryButtonText="$t('save_changes')"
		@primaryButtonAction="saveAction"
		@closed="closeAction"
	>
		<template #body>
			<v-form ref="form" v-model="valid" v-if="currentItem">
				<localized-text-field
					v-model="currentItem.name"
					:defaultLanguage="companyDefaultLanguage"
					:options="companyLanguageOptions"
					:rules="rules.name"
					context="product"
					:label="$t('name')"
				/>
				<v-row class="ma-0">
					<v-col cols="8" class="pa-0">
						<product-group-select
							v-model="currentItem.productGroupId"
							clearable
							:label="$t('product_group')"
							:eager="true"
							:rules="rules.productGroupId"
						/>
					</v-col>
					<v-col cols="4" class="pa-0 pl-4">
						<v-text-field v-model="currentItem.code" :label="$t('code')" dense :rules="rules.code" />
					</v-col>
				</v-row>

				<v-col cols="12" class="pa-0">
					<v-autocomplete
						:label="$t('units')"
						v-model="currentItem.unitCodes"
						multiple
						outlined
						closable-chips
						:items="unitOptions"
						:rules="rules.unit"
						item-value="code"
						:item-text="(i) => $t(`$unit.${i.name}`)"
						append-icon="icon-angle-down"
						:loading="!unitOptions"
					>
						<template #selection="{ item }">
							<v-chip close @click:close="removeItem(item)">{{ $t(`$unit.${item.name}`) }}</v-chip>
						</template>
						<template #item="{ item }">
							<v-chip>{{ $t(`$unit.${item.name}`) }}</v-chip>
						</template>
					</v-autocomplete>
				</v-col>
			</v-form>
		</template>
	</ext-dialog>
</template>

<script>
import ExtDialog from "../../../partials/dialogs/ExtDialog.vue";
import ProductGroupSelect from "../../../controls/ProductGroupSelect.vue";
import LocalizedTextField from "../../../controls/LocalizedTextField.vue";
import remote from "../../../../data/remote";
import uiMixin from "../../../../store/interfaces/ui.mixin";
import { mapGetters } from "vuex";

export default {
	name: "ProductDialog",
	components: { ExtDialog, ProductGroupSelect, LocalizedTextField },
	mixins: [uiMixin],
	props: { value: Boolean, item: Object },
	data() {
		return {
			visible: false,
			valid: true,
			currentItem: null,
			unitOptions: [],
			itemTemplate: {
				name: {},
				code: "",
				units: [],
				unitCodes: [],
				productGroupId: null,
				attributes: [],
				variants: [],
				images: [],
				description: {},
			},
			rules: {
				name: [
					(v) =>
						v
							? Object.values(v).some((s) => s.length > 100)
								? this.$t("$validation.max_length", { item: this.$t("name"), length: "100" })
								: true
							: this.$t("$validation.required", { item: this.$t("name") }),
				],
				productGroupId: [(v) => Boolean(v) || this.$t("$validation.required", { item: this.$t("product_group") })],
				unit: [(v) => Boolean(v.length) || this.$t("$validation.required", { item: this.$t("unit") })],
				code: [
					(v) =>
						v
							? v.length > 20
								? this.$t("$validation.max_length", { item: this.$t("code"), length: "20" })
								: true
							: this.$t("$validation.required", { item: this.$t("code") }),
				],
			},
		};
	},
	computed: {
		...mapGetters(["companyDefaultLanguage", "companyLanguageOptions"]),
		dialogTitle() {
			return this.currentItem?._id ? this.$t("edit_product") : this.$t("add_new_product");
		},
	},
	watch: {
		value(v) {
			this.visible = v;
		},
		visible(v) {
			this.$emit("input", v);
			if (v) {
				const base = this.item || this.itemTemplate;
				if (!base.name[this.companyDefaultLanguage]) base.name[this.companyDefaultLanguage] = "";
				for (const l of this.companyLanguageOptions) if (!base.name[l]) base.name[l] = "";
				this.currentItem = { ...base, name: { ...base.name }, unitCodes: (base.units || []).map((u) => u.code) };
				if (!this.unitOptions.length) {
					remote.structures.getUnits({
						onSuccess: (r) => (this.unitOptions = r.items),
					});
				}
			} else {
				this.$refs.form?.reset();
				this.currentItem = null;
			}
		},
	},
	methods: {
		removeItem(item) {
			const i = this.currentItem.unitCodes.indexOf(item.code);
			if (i > -1) this.currentItem.unitCodes.splice(i, 1);
		},
		saveAction() {
			if (!this.valid) {
				this.$refs.form.validate();
				return;
			}
			if (this.currentItem.name[this.companyDefaultLanguage] === "") {
				return this.showAlertDialog({
					message:
						this.$t("$validation.required_for_default_language", { item: this.$t("name") }) +
						` (${this.$t(`$language.${this.companyDefaultLanguage}`)})`,
				});
			}

			const originalUnitCodes = (this.item && this.item.units ? this.item.units : []).map((u) => u.code);

			const removed = originalUnitCodes.filter((c) => !this.currentItem.unitCodes.includes(c));
			if (removed.length) {
				const inUse = (this.currentItem.variants || []).filter((v) => v.units.some((u) => removed.includes(u.code)));
				if (inUse.length) {
					return this.showAlertDialog({
						message: this.$t("Cannot remove unit(s) {units} because they are used by existing variants.", {
							units: removed.join(", "),
						}),
					});
				}
			}

			this.currentItem.units = this.currentItem.unitCodes
				.map((c) => this.unitOptions.find((u) => u.code === c))
				.filter(Boolean);

			this.showProgressDialog();
			const cb = {
				onSuccess: (r) => {
					this.hideProgressDialog();
					this.showSnackBar({
						message: this.$t(this.currentItem._id ? "$message.updated" : "$message.created", {
							item: this.$t("product"),
						}),
					});
					this[this.currentItem._id ? "$editEvent" : "$addEvent"]("product");
					this.$emit("save", r);
					this.$emit("input", false);
				},
				onFail: () => this.hideProgressDialog(),
			};
			this.currentItem._id
				? remote.products.update(this.currentItem, cb)
				: remote.products.create(this.currentItem, cb);
		},
		deleteAction() {
			this.showConfirmDialog({
				message: this.$t("delete_confirm_message"),
				onConfirm: () => {
					this.showProgressDialog();
					remote.products.delete(this.currentItem._id, {
						onSuccess: (result) => {
							this.hideProgressDialog();
							this.showSnackBar({
								message: this.$t("$message.deleted", {
									item: this.$t("product"),
								}),
							});
							this.$emit("deleted", result);
						},
					});
				},
			});
		},
		closeAction() {
			this.visible = false;
		},
	},
	created() {
		this.visible = this.value;
	},
};
</script>
